/**
 * Shared styled components and utilities for bulk CSV upload/invite modals.
 * Used by BulkInviteModal and BulkClientUploadModal.
 */

import styled from 'styled-components';

// ============================================================================
// Drop Zone
// ============================================================================

export const DropZone = styled.div<{ $dragOver: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[10]};
  border: 2px dashed
    ${({ theme, $dragOver }) =>
      $dragOver ? theme.colors.primary.base : theme.colors.borders.medium};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  background: ${({ theme, $dragOver }) =>
    $dragOver ? theme.colors.status.info.bg : theme.colors.backgrounds.page};
  cursor: pointer;
  transition: ${({ theme }) => theme.transitions.fast};
  text-align: center;

  &:hover {
    border-color: ${({ theme }) => theme.colors.primary.base};
    background: ${({ theme }) => theme.colors.status.info.bg};
  }

  svg {
    width: 40px;
    height: 40px;
    color: ${({ theme }) => theme.colors.primary.base};
  }
`;

export const DropLabel = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.base};
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  margin: 0;
`;

export const DropSubLabel = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

export const FileNameChip = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  background: ${({ theme }) => theme.colors.status.info.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.info.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.info.text};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
`;

// ============================================================================
// Summary Bar
// ============================================================================

export const SummaryBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[4]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
`;

const SummaryItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[1]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};

  svg {
    width: 16px;
    height: 16px;
  }
`;

export const ValidCount = styled(SummaryItem)`
  color: ${({ theme }) => theme.colors.status.success.text};
`;

export const InvalidCount = styled(SummaryItem)`
  color: ${({ theme }) => theme.colors.status.error.text};
`;

export const TotalCount = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
`;

// ============================================================================
// Preview Table
// ============================================================================

export const TableWrapper = styled.div`
  border: 1px solid ${({ theme }) => theme.colors.borders.light};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  overflow: hidden;
  max-height: 320px;
  overflow-y: auto;
`;

export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

export const Th = styled.th`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  text-align: left;
  font-size: ${({ theme }) => theme.typography.fontSize.xs};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.secondary};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: ${({ theme }) => theme.colors.backgrounds.page};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  white-space: nowrap;
  position: sticky;
  top: 0;
`;

export const Td = styled.td<{ $invalid?: boolean }>`
  padding: ${({ theme }) => `${theme.spacing[2]} ${theme.spacing[3]}`};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme, $invalid }) =>
    $invalid ? theme.colors.status.error.text : theme.colors.text.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.borders.light};
  vertical-align: middle;
`;

export const Tr = styled.tr<{ $invalid?: boolean }>`
  background: ${({ theme, $invalid }) =>
    $invalid ? theme.colors.status.error.bg : 'transparent'};

  &:last-child td {
    border-bottom: none;
  }
`;

export const StatusIcon = styled.span<{ $valid: boolean }>`
  display: inline-flex;
  align-items: center;
  color: ${({ theme, $valid }) =>
    $valid ? theme.colors.status.success.text : theme.colors.status.error.text};

  svg {
    width: 16px;
    height: 16px;
  }
`;

// ============================================================================
// Feedback Banners
// ============================================================================

export const ErrorBanner = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[2]};
  padding: ${({ theme }) => theme.spacing[3]};
  background: ${({ theme }) => theme.colors.status.error.bg};
  border: 1px solid ${({ theme }) => theme.colors.status.error.border};
  border-radius: ${({ theme }) => theme.borderRadius.md};
  margin-bottom: ${({ theme }) => theme.spacing[4]};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.status.error.text};

  svg {
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
`;

export const ResultBanner = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
  padding: ${({ theme }) => theme.spacing[8]};
  text-align: center;
`;

export const ResultIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.status.success.bg};
  color: ${({ theme }) => theme.colors.status.success.text};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 24px;
    height: 24px;
  }
`;

export const ResultTitle = styled.h3`
  font-size: ${({ theme }) => theme.typography.fontSize.lg};
  font-weight: ${({ theme }) => theme.typography.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`;

export const ResultSubtext = styled.p`
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin: 0;
`;

// ============================================================================
// Modal Layout
// ============================================================================

export const ModalFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing[3]};
  padding-top: ${({ theme }) => theme.spacing[4]};
  border-top: 1px solid ${({ theme }) => theme.colors.borders.light};
  margin-top: ${({ theme }) => theme.spacing[2]};
`;

export const FooterRight = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing[3]};
`;

export const LoadingState = styled.div`
  padding: ${({ theme }) => theme.spacing[12]};
  text-align: center;
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.sm};
`;

